import { supabase } from '../utils/supabase';

/**
 * Saved Vendors Service
 * Handles saving/favoriting vendors
 */
class SavedVendorsService {
    /**
     * Get user's saved vendors
     */
    async getSavedVendors(userId) {
        try {
            const { data, error } = await supabase
                .from('saved_vendors')
                .select(`
          *,
          vendor_profiles!vendor_id (
            id,
            business_name,
            category,
            city,
            state,
            average_rating,
            total_reviews,
            starting_price,
            cover_image,
            profile_image,
            is_verified
          )
        `)
                .eq('user_id', userId)
                .order('created_at', { ascending: false });

            if (error) throw error;

            return {
                success: true,
                data: data || []
            };
        } catch (error) {
            console.error('Error fetching saved vendors:', error);
            return {
                success: false,
                error: error.message,
                data: []
            };
        }
    }

    /**
     * Check if vendor is saved by user
     */
    async isVendorSaved(userId, vendorId) {
        try {
            const { data, error } = await supabase
                .from('saved_vendors')
                .select('id')
                .eq('user_id', userId)
                .eq('vendor_id', vendorId)
                .single();

            if (error && error.code !== 'PGRST116') throw error;

            return {
                success: true,
                isSaved: !!data
            };
        } catch (error) {
            console.error('Error checking saved vendor:', error);
            return {
                success: false,
                isSaved: false
            };
        }
    }

    /**
     * Save a vendor
     */
    async saveVendor(userId, vendorId) {
        try {
            const { data, error } = await supabase
                .from('saved_vendors')
                .insert({
                    user_id: userId,
                    vendor_id: vendorId
                })
                .select()
                .single();

            if (error) throw error;

            return {
                success: true,
                data
            };
        } catch (error) {
            console.error('Error saving vendor:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Unsave a vendor
     */
    async unsaveVendor(userId, vendorId) {
        try {
            const { error } = await supabase
                .from('saved_vendors')
                .delete()
                .eq('user_id', userId)
                .eq('vendor_id', vendorId);

            if (error) throw error;

            return {
                success: true
            };
        } catch (error) {
            console.error('Error unsaving vendor:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Toggle saved status
     */
    async toggleSaveVendor(userId, vendorId) {
        try {
            const checkResult = await this.isVendorSaved(userId, vendorId);

            if (checkResult.isSaved) {
                return await this.unsaveVendor(userId, vendorId);
            } else {
                return await this.saveVendor(userId, vendorId);
            }
        } catch (error) {
            console.error('Error toggling saved vendor:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }
}

export default new SavedVendorsService();
